<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { FilterIcon, CloseIcon } from "../icons";
import { vPosition } from "../directives/geometry";
import type { Field, QuerySpec } from "@yawhub/core";
const props = defineProps<{
  field: Field;
  query: QuerySpec;
  disabled?: boolean;
}>();
const emit = defineEmits<{ apply: [QuerySpec] }>();
const expanded = ref(false);
const panel = ref<HTMLElement>(),
  op = ref("eq"),
  value = ref<string | boolean>(""),
  left = ref(0),
  top = ref(0);
const active = computed(() =>
  props.query.filters.some((f) => f.fieldId === props.field.id),
);
const options = computed(() => [
  ["eq", "er lig med"],
  ["ne", "er forskellig fra"],
  ...(["text", "enum"].includes(props.field.type)
    ? [["contains", "indeholder"]]
    : []),
  ...(["integer", "decimal", "date", "datetime"].includes(props.field.type)
    ? [
        ["gt", "er større end / efter"],
        ["gte", "er mindst / fra"],
        ["lt", "er mindre end / før"],
        ["lte", "er højst / til"],
      ]
    : []),
  ["is_null", "er tom"],
  ["is_not_null", "er udfyldt"],
]);
async function open(event: MouseEvent) {
  const filter = props.query.filters.find((f) => f.fieldId === props.field.id);
  op.value =
    filter && options.value.some((o) => o[0] === filter.op) ? filter.op : "eq";
  value.value =
    typeof filter?.value === "boolean"
      ? filter.value
      : typeof filter?.value === "string"
        ? filter.value
        : props.field.type === "boolean"
          ? true
          : "";
  if (props.field.type === "datetime" && value.value) {
    const d = new Date(String(value.value));
    value.value = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  }
  const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
  left.value = Math.max(12, Math.min(r.left, innerWidth - 332));
  top.value = Math.max(12, Math.min(r.bottom + 6, innerHeight - 330));
  await nextTick();
  panel.value?.showPopover();
  panel.value?.querySelector("select")?.focus();
}
function apply(remove = false) {
  const filters = props.query.filters.filter(
    (f) => f.fieldId !== props.field.id,
  );
  if (!remove)
    filters.push({
      fieldId: props.field.id,
      op: op.value as QuerySpec["filters"][number]["op"],
      ...(!op.value.startsWith("is_")
        ? {
            value: ["integer", "decimal", "rating"].includes(props.field.type)
              ? String(value.value).replace(",", ".")
              : props.field.type === "datetime"
                ? new Date(String(value.value)).toISOString()
                : value.value,
          }
        : {}),
    });
  emit("apply", { ...props.query, filters, cursor: undefined });
  panel.value?.hidePopover();
}
</script>
<template>
  <button
    type="button"
    class="column-filter-trigger"
    :class="{ filtered: active }"
    :disabled="disabled || !field.queryable || field.type === 'json'"
    :aria-expanded="expanded"
    :aria-label="'Filtrér ' + field.label"
    @click="open"
  >
    {{ field.label }}<span v-if="field.unit" class="unit">{{ field.unit }}</span
    ><FilterIcon v-if="field.queryable" :size="13" />
  </button>
  <div
    ref="panel"
    popover="auto"
    @toggle="expanded = ($event as ToggleEvent).newState === 'open'"
    class="column-filter-panel"
    v-position="{ x: left, y: top }"
  >
    <div class="section-heading">
      <h3>Filtrér {{ field.label }}</h3>
      <button
        type="button"
        class="icon-button"
        aria-label="Luk filter"
        @click="panel?.hidePopover()"
      >
        <CloseIcon :size="17" />
      </button>
    </div>
    <form class="form-stack" @submit.prevent="apply()">
      <label
        >Betingelse<select v-model="op">
          <option v-for="o in options" :key="o[0]" :value="o[0]">
            {{ o[1] }}
          </option>
        </select></label
      >
      <label v-if="!op.startsWith('is_')"
        >Værdi<select v-if="field.type === 'boolean'" v-model="value">
          <option :value="true">Ja</option>
          <option :value="false">Nej</option></select
        ><select
          v-else-if="field.type === 'enum' && op !== 'contains'"
          v-model="value"
          required
        >
          <option disabled value="">Vælg værdi</option>
          <option v-for="option in field.options" :key="option">
            {{ option }}
          </option></select
        ><input
          v-else
          v-model="value"
          required
          :type="
            field.type === 'date'
              ? 'date'
              : field.type === 'datetime'
                ? 'datetime-local'
                : 'text'
          "
          :inputmode="
            ['decimal', 'integer'].includes(field.type) ? 'decimal' : undefined
          "
      /></label>
      <div class="form-actions">
        <button
          v-if="active"
          type="button"
          class="text-button"
          @click="apply(true)"
        >
          Fjern filter</button
        ><button
          class="button primary"
          :disabled="!active && query.filters.length >= 20"
        >
          Anvend filter
        </button>
      </div>
    </form>
  </div>
</template>
<style scoped lang="scss" src="../styles/components/ColumnFilter.scss"></style>
